import { AnalyticsEvent, logAnalyticsEvent } from 'utils/analytics/analyticsUtils'
import React, { useCallback, useEffect, useRef, useState } from 'react'

import { CircularIconButton } from 'components/common/Gallery/GalleryImage'
import { GRAY_800 } from 'constants/styling/theme'
import { MUIInputField } from 'components/common/MUIInputField'
import ThumbDownOutlinedIcon from '@mui/icons-material/ThumbDownOutlined'
import ThumbUpAltOutlinedIcon from '@mui/icons-material/ThumbUpAltOutlined'
import { useActionPopup } from 'utils/hooks'
import { useSnackbar } from 'components/contexts/SnackbarService.context'
import { useTranslation } from 'react-i18next'
import { useUserData } from 'components/contexts/UserDataContext'

/** Enum representing the client rating options for a photo. */
enum ClientRating {
  THUMB_UP,
  THUMB_DOWN,
}

/** @interface Props for the ClientRatingPhotoIcons component. */
interface Props {
  /** The ID of the visual. */
  visualId?: string
  /** The ID of the assignment. */
  assignmentId?: string
}

/** FeedbackInput is a memoized component that renders a multiline input field for providing feedback. */
const FeedbackInput = React.memo(({ setFeedbackText }: { setFeedbackText: React.Dispatch<React.SetStateAction<string>> }) => {
  const { t } = useTranslation(['gallery'])

  return (
    <MUIInputField
      isMultiline
      label={t('client_gallery.rating_ai_editing_popup.label')}
      placeholder={t('client_gallery.rating_ai_editing_popup.placeholder')}
      onChange={(e) => setFeedbackText(e.target.value)}
    />
  )
})

/**
 * @component
 * `ClientRatingPhotoIcons` renders two circular icon buttons for submitting user ratings (thumbs-up or thumbs-down)
 * for submitting user ratings (thumbs-up or thumbs-down) on a visual element. It also handles
 * optional feedback submission to Mixpanel.
 *
 * @example
 * <ClientRatingPhotoIcons
 *   visualId="12345"
 *   assignmentId="67890"
 * />
 */
export const ClientRatingPhotoIcons: React.FC<Props> = ({
  visualId,
  assignmentId,
}) => {
  const { clientData } = useUserData()
  const { t } = useTranslation(['gallery', 'common'])
  const { showConfirm } = useActionPopup()
  const { spawnSuccessToast } = useSnackbar()

  const [feedbackText, setFeedbackText] = useState('')
  const feedbackTextRef = useRef(feedbackText)

  // Keep the ref updated with the latest feedbackText value
  useEffect(() => {
    feedbackTextRef.current = feedbackText
  }, [feedbackText])

  /**
   * Handles the rating of a client photo, allowing users to provide feedback
   * and logging the rating event for analytics purposes.
   */
  const handleRatingClick = useCallback(async (rating: ClientRating) => {

    // If the user selects THUMB_DOWN, a confirmation dialog is displayed with an optional feedback input.
    if (rating === ClientRating.THUMB_DOWN) {
      const confirmResponse = await showConfirm(
        <FeedbackInput setFeedbackText={setFeedbackText} />,
        {
          title: t('client_gallery.rating_ai_editing_popup.title'),
          confirmDenyText: t('common:Cancel'),
          confirmAcceptText: t('common:Submit'),
        }
      )

      if (!confirmResponse) {
        setFeedbackText('') // Clear feedbackText if the user cancels
        return
      }
    }

    logAnalyticsEvent(AnalyticsEvent.AI_EDITED_VISUAL_RATING, {
      assignmentId,
      visualId,
      userId: clientData?.id,
      rating: rating === ClientRating.THUMB_UP ? 'Thumbs-up' : 'Thumbs-down',
      optionalFeedback: feedbackTextRef.current, // Use the latest feedbackText value from the ref
      timestamp: new Date().toISOString()
    })

    spawnSuccessToast(
      t('client_gallery.rating_ai_editing_popup.success_message')
    )

    // Clear feedbackText after submission
    setFeedbackText('')
  }, [assignmentId, clientData?.id, showConfirm, spawnSuccessToast, t, visualId])

  return (
    <>
      <CircularIconButton
        icon={
          <ThumbUpAltOutlinedIcon
            fontSize="medium"
            sx={{ color: GRAY_800 }}
            onClick={() => handleRatingClick(ClientRating.THUMB_UP)}
          />
        }
      />
      <CircularIconButton
        icon={
          <ThumbDownOutlinedIcon
            fontSize="medium"
            sx={{ color: GRAY_800 }}
            onClick={() => handleRatingClick(ClientRating.THUMB_DOWN)}
          />
        }
      />
    </>
  )
}
